/**
 * AA-UI 1 - Admin Analytics Dashboard v1
 * Comprehensive analytics visualization for administrators
 * Features: Grand totals, daily stats, department filtering, resolver activity monitoring, staff directory
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { authAPI, analyticsAPI, configAPI } from '@/lib/api';
import { POLLING_INTERVALS, HTTP_STATUS } from '@/lib/constants';
import InterfaceSwitcher from '@/components/InterfaceSwitcher';
import ReportDownloadPanel from '@/components/ReportDownloadPanel';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  Clock, 
  CheckCircle2, 
  AlertTriangle,
  RefreshCw,
  LogOut,
  Filter,
  Calendar,
  Building2,
  UserCheck,
  UserX,
  Activity,
  PieChart as PieChartIcon,
  ArrowUp,
  ArrowDown,
  Search,
  Mail,
  Briefcase,
  BookUser,
  Gauge,
  MessageSquare
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import AdvancedAnalyticsSection from '@/components/AdvancedAnalyticsSection';
import StatusDrilldownModal from '@/components/StatusDrilldownModal';
import FeedbackAnalyticsSection from '@/components/FeedbackAnalyticsSection';
import AdvancedTicketGraphs from '@/components/AdvancedTicketGraphs';

// Same config as the CAO War Room — colour-coded tiles for every tracked status.
const STATUS_TILE_CONFIG = [
  { key: 'Open', bg: 'bg-sky-500/10', border: 'border-sky-500/30', labelColor: 'text-sky-300', countColor: 'text-white', tooltip: 'Newly created tickets not yet acknowledged.' },
  { key: 'Pending', bg: 'bg-yellow-500/10', border: 'border-yellow-500/40', labelColor: 'text-yellow-300', countColor: 'text-white', tooltip: 'Open tickets older than 12 hours with no resolver action — needs attention.' },
  { key: 'Acknowledged', bg: 'bg-indigo-500/10', border: 'border-indigo-500/30', labelColor: 'text-indigo-300', countColor: 'text-white', tooltip: 'Seen by the resolver but work not started yet.' },
  { key: 'In Progress', bg: 'bg-amber-500/10', border: 'border-amber-500/30', labelColor: 'text-amber-300', countColor: 'text-white', tooltip: 'Resolver actively working on the ticket.' },
  { key: 'Resolved', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', labelColor: 'text-emerald-300', countColor: 'text-white', tooltip: 'Work completed. Awaiting feedback or closed.' },
  { key: 'SLA BREACHED', bg: 'bg-red-500/10', border: 'border-red-500/40', labelColor: 'text-red-300', countColor: 'text-white', tooltip: 'Crossed Resolution TAT. Escalations active.' },
  { key: 'On Leave', bg: 'bg-cyan-500/10', border: 'border-cyan-500/30', labelColor: 'text-cyan-300', countColor: 'text-white', tooltip: 'Handled by a fill-in resolver (original on leave).' },
  { key: 'Shift Completed', bg: 'bg-purple-500/10', border: 'border-purple-500/30', labelColor: 'text-purple-300', countColor: 'text-white', tooltip: 'Handover to next-shift staff done.' },
  { key: 'Purchase Material Requested', bg: 'bg-orange-500/10', border: 'border-orange-500/30', labelColor: 'text-orange-300', countColor: 'text-white', tooltip: 'Material requisition raised.' },
  { key: 'Purchase Material Approved', bg: 'bg-lime-500/10', border: 'border-lime-500/30', labelColor: 'text-lime-300', countColor: 'text-white', tooltip: 'Stores/Accounts approved the requisition.' },
  { key: 'Purchase Material Received', bg: 'bg-teal-500/10', border: 'border-teal-500/30', labelColor: 'text-teal-300', countColor: 'text-white', tooltip: 'Material received — work can resume.' },
  { key: 'Ticket Diverted', bg: 'bg-fuchsia-500/10', border: 'border-fuchsia-500/30', labelColor: 'text-fuchsia-300', countColor: 'text-white', tooltip: 'Ticket rerouted to a different department.' },
  { key: 'Long Term Vacations', bg: 'bg-cyan-500/10', border: 'border-cyan-500/30', labelColor: 'text-cyan-300', countColor: 'text-white', tooltip: 'Resolver on extended leave; work paused.' },
  { key: 'Ticket Closed', bg: 'bg-slate-500/20', border: 'border-slate-500/40', labelColor: 'text-slate-300', countColor: 'text-white', tooltip: 'Archival close — hidden from creators & resolvers; visible to admins only.' },
];

const SOURCE_COLORS = { App: '#E97832', Email: '#334155', Admin: '#0EA5E9', Unknown: '#64748B' };

const formatBreachHours = (hours) => {
  if (!hours || hours <= 0) return { label: '—', tooltip: 'No breach→resolved samples yet' };
  const days = Math.floor(hours / 24);
  const remHours = Math.floor(hours % 24);
  const mins = Math.round((hours - Math.floor(hours)) * 60);
  const tooltip = days > 0 ? `${days}d ${remHours}h ${mins}m` : `${remHours}h ${mins}m`;
  return { label: `${hours.toFixed(1)} h`, tooltip };
};

const AdminAnalyticsDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [dailyStats, setDailyStats] = useState(null);
  const [resolverActivity, setResolverActivity] = useState([]);
  const [inactiveResolvers, setInactiveResolvers] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [activeTab, setActiveTab] = useState('overview');
  const [staffDirectory, setStaffDirectory] = useState([]);
  const [staffSearch, setStaffSearch] = useState('');
  const [staffCount, setStaffCount] = useState(0);
  // Drilldown modal — opens when an admin clicks a status tile
  const [drilldownStatus, setDrilldownStatus] = useState(null);

  // Department Performance Ranking — auto-refresh on a 25-second cadence
  // per the May 14, 2026 spec ("delay time of every 20 to 30 seconds").
  // `rankingRefreshing` flashes the live badge; `rankingRefreshIn` powers
  // the countdown so admins know exactly when fresh data lands.
  const RANKING_REFRESH_MS = 25000;
  const [rankingRefreshing, setRankingRefreshing] = useState(false);
  const [rankingRefreshIn, setRankingRefreshIn] = useState(Math.round(RANKING_REFRESH_MS / 1000));

  const fetchData = useCallback(async () => {
    try {
      const userRes = await authAPI.getMe();
      setUser(userRes.data);
      
      if (userRes.data.role !== 'cao') {
        toast.error('Access denied. CAO only.');
        navigate('/dashboard');
        return;
      }
      
      // Fetch all analytics data including staff directory
      const [analyticsRes, dailyRes, activityRes, staffRes] = await Promise.all([
        analyticsAPI.getOverview(),
        analyticsAPI.getDailyStats(selectedDate),
        analyticsAPI.getResolverActivity(),
        configAPI.getStaffDirectory()
      ]);
      
      setAnalytics(analyticsRes.data);
      setDailyStats(dailyRes.data);
      setResolverActivity(activityRes.data.active_resolvers || []);
      setInactiveResolvers(activityRes.data.inactive_resolvers || []);
      setStaffDirectory(staffRes.data.staff || []);
      setStaffCount(staffRes.data.staff_count || 0);
      
    } catch (error) {
      if (error.response?.status === HTTP_STATUS.UNAUTHORIZED) {
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, selectedDate]);

  useEffect(() => {
    fetchData();
    // Auto-refresh based on configured interval
    const interval = setInterval(fetchData, POLLING_INTERVALS.ANALYTICS_REFRESH);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Dedicated 25-second refresh for the Department Performance Ranking
  // table (independent from the general analytics polling cadence so the
  // ranking stays fresh without thrashing the staff directory / daily
  // stats payloads). Only re-fetches the overview endpoint.
  const fetchRanking = useCallback(async () => {
    setRankingRefreshing(true);
    try {
      const res = await analyticsAPI.getOverview();
      setAnalytics(res.data);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        console.debug('Ranking refresh failed:', e?.message);
      }
    } finally {
      setRankingRefreshing(false);
      setRankingRefreshIn(Math.round(RANKING_REFRESH_MS / 1000));
    }
  }, []);

  // Manual "Refresh" button — runs the same analyze pipeline as the
  // automatic 25s polling but with a deliberate 25-second "analyzing"
  // window (within the 20–30s spec window) so admins see a clear waiting
  // state. The fetch fires immediately; the loader is held for the full
  // window so any straggling backend aggregation has time to settle.
  const MANUAL_ANALYZE_MS = 25000;
  const handleManualRefresh = useCallback(async () => {
    if (rankingRefreshing) return;
    setRankingRefreshing(true);
    const minDelay = new Promise((resolve) => setTimeout(resolve, MANUAL_ANALYZE_MS));
    try {
      const res = await analyticsAPI.getOverview();
      await minDelay;
      setAnalytics(res.data);
    } catch (e) {
      await minDelay;
      if (process.env.NODE_ENV !== 'production') {
        console.debug('Manual ranking refresh failed:', e?.message);
      }
    } finally {
      setRankingRefreshing(false);
      setRankingRefreshIn(Math.round(RANKING_REFRESH_MS / 1000));
    }
  }, [rankingRefreshing]);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      await fetchRanking();
    };
    const refresh = setInterval(tick, RANKING_REFRESH_MS);
    const countdown = setInterval(() => {
      setRankingRefreshIn((s) => (s <= 1 ? Math.round(RANKING_REFRESH_MS / 1000) : s - 1));
    }, 1000);
    return () => {
      cancelled = true;
      clearInterval(refresh);
      clearInterval(countdown);
    };
  }, [fetchRanking]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success('Analytics refreshed');
  };

  const handleLogout = async () => {
    try {
      await authAPI.logout();
      navigate('/');
    } catch (error) {
      navigate('/');
    }
  };

  // Filter department stats
  const getFilteredDepartmentStats = () => {
    if (!analytics?.department_stats) return [];
    if (selectedDepartment === 'all') return analytics.department_stats;
    return analytics.department_stats.filter(d => d.department === selectedDepartment);
  };

  // Get unique departments for filter
  const getDepartments = () => {
    if (!analytics?.department_stats) return [];
    return analytics.department_stats.map(d => d.department);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-12 h-12 border-4 border-[#E97832] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#1e3a5f] to-[#0f172a] text-white sticky top-0 z-50 shadow-lg border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#E97832] to-[#EA580C] flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Admin Analytics Dashboard</h1>
                <p className="text-sm text-slate-300">Real-time insights & performance metrics</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <InterfaceSwitcher user={user} currentInterface="cao" />
              <Button
                data-testid="fb-nav-btn"
                variant="ghost"
                size="sm"
                onClick={() => navigate('/feedback')}
                className="text-white hover:bg-white/10"
                title="Feedback (FB)"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Feedback
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/cao')}
                className="text-white hover:bg-white/10"
              >
                <Activity className="w-4 h-4 mr-2" />
                War Room
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleRefresh}
                disabled={refreshing}
                className="text-white hover:bg-white/10"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout}
                className="text-white hover:bg-white/10"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Grand Total Stats - Row 1 */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <PieChartIcon className="w-5 h-5 text-[#E97832]" />
            Overall Grand Totals
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Total Tickets Created */}
            <Card className="bg-gradient-to-br from-blue-600 to-blue-700 border-0 shadow-xl">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-sm font-medium">Total Tickets Created</p>
                    <p className="text-4xl font-bold text-white mt-1">{analytics?.total_tickets || 0}</p>
                    <p className="text-blue-200 text-xs mt-2">By all ticket creators</p>
                  </div>
                  <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
                    <TrendingUp className="w-7 h-7 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Total Allocations */}
            <Card className="bg-gradient-to-br from-purple-600 to-purple-700 border-0 shadow-xl">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-100 text-sm font-medium">Total Allocations</p>
                    <p className="text-4xl font-bold text-white mt-1">{analytics?.total_tickets || 0}</p>
                    <p className="text-purple-200 text-xs mt-2">To {analytics?.department_stats?.length || 0} departments</p>
                  </div>
                  <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
                    <Building2 className="w-7 h-7 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Open Tickets */}
            <Card className="bg-gradient-to-br from-amber-500 to-orange-600 border-0 shadow-xl" data-testid="admin-open-tickets">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-amber-100 text-sm font-medium">Pending &amp; Open</p>
                    <p className="text-4xl font-bold text-white mt-1">{analytics?.open_tickets || 0}</p>
                    <p className="text-amber-200 text-xs mt-2">Actionable tickets only</p>
                  </div>
                  <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
                    <Clock className="w-7 h-7 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Resolved Tickets */}
            <Card className="bg-gradient-to-br from-emerald-500 to-green-600 border-0 shadow-xl">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-emerald-100 text-sm font-medium">Resolved Tickets</p>
                    <p className="text-4xl font-bold text-white mt-1">{analytics?.resolved_tickets || 0}</p>
                    <p className="text-emerald-200 text-xs mt-2">
                      {analytics?.total_tickets > 0 
                        ? `${Math.round((analytics.resolved_tickets / analytics.total_tickets) * 100)}% completion`
                        : '0% completion'
                      }
                    </p>
                  </div>
                  <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
                    <CheckCircle2 className="w-7 h-7 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Ticket Status Distribution - full spectrum tiles */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-[#E97832]" />
            Ticket Status Distribution
          </h2>
          <Card className="bg-slate-800 border-slate-700" data-testid="admin-status-distribution">
            <CardContent className="p-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                {STATUS_TILE_CONFIG.map((cfg) => {
                  const count = analytics?.status_counts?.[cfg.key] || 0;
                  return (
                    <button
                      key={cfg.key}
                      type="button"
                      data-testid={`admin-status-tile-${cfg.key.toLowerCase().replace(/\s+/g, '-')}`}
                      onClick={() => setDrilldownStatus(cfg.key)}
                      className={`text-left rounded-lg border px-3 py-2 ${cfg.bg} ${cfg.border} hover:ring-2 hover:ring-[#E97832]/60 hover:scale-[1.02] transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#E97832]`}
                      title={`${cfg.tooltip} Click to inspect tickets.`}
                    >
                      <p className={`text-xs font-medium ${cfg.labelColor}`}>{cfg.key}</p>
                      <p className={`text-xl font-bold ${cfg.countColor}`}>{count}</p>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-slate-500 mt-3">
                Click a tile to drill into the underlying tickets. &quot;Pending&quot; counts Open tickets that have not moved in over 12 hours; &quot;On Leave&quot; is derived from tickets currently reassigned to a fill-in resolver.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Advanced Ticket Analytics — replaces the older Source Analysis pie */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#E97832]" />
            Advanced Ticket Analytics
          </h2>
          <Card className="bg-slate-100 border-slate-300 text-slate-900" data-testid="admin-advanced-graphs">
            <CardContent className="p-4">
              <AdvancedTicketGraphs />
            </CardContent>
          </Card>
        </div>

        {/* Daily Stats - Row 2 */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#E97832]" />
              Daily Statistics
            </h2>
            <div className="flex items-center gap-3">
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="bg-slate-800 border-slate-600 text-white w-40"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* New Tickets Today */}
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">New Tickets Today</p>
                    <p className="text-3xl font-bold text-white mt-1">{dailyStats?.new_tickets || 0}</p>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <ArrowUp className="w-6 h-6 text-blue-400" />
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-2">Created by ticket creators</p>
              </CardContent>
            </Card>

            {/* Allocations Today */}
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">Allocations Today</p>
                    <p className="text-3xl font-bold text-white mt-1">{dailyStats?.allocations || 0}</p>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <Users className="w-6 h-6 text-purple-400" />
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-2">To {dailyStats?.departments_count || 0} departments</p>
              </CardContent>
            </Card>

            {/* Work in Progress Today */}
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">Work in Progress</p>
                    <p className="text-3xl font-bold text-white mt-1">{dailyStats?.in_progress || 0}</p>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-amber-500/20 flex items-center justify-center">
                    <Activity className="w-6 h-6 text-amber-400" />
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-2">Being worked on</p>
              </CardContent>
            </Card>

            {/* Resolved Today */}
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">Resolved Today</p>
                    <p className="text-3xl font-bold text-white mt-1">{dailyStats?.resolved || 0}</p>
                  </div>
                  <div className="w-12 h-12 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-emerald-400" />
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-2">Completed today</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Inactive Resolvers Alert */}
        {inactiveResolvers.length > 0 && (
          <Card className="bg-red-900/30 border-red-700 mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-red-400 flex items-center gap-2 text-base">
                <AlertTriangle className="w-5 h-5" />
                Inactive Ticket Resolvers Alert
              </CardTitle>
              <CardDescription className="text-red-300/70">
                These resolvers have not checked their allocated tasks recently
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {inactiveResolvers.map((resolver) => (
                  <div key={`inactive-${resolver.email || resolver.name}`} className="bg-red-950/50 rounded-lg p-3 border border-red-800">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-red-800 flex items-center justify-center">
                        <UserX className="w-5 h-5 text-red-300" />
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">{resolver.name}</p>
                        <p className="text-red-400 text-xs">{resolver.department}</p>
                        <p className="text-red-300/60 text-xs">
                          {resolver.pending_tickets} pending | Last active: {resolver.last_active || 'Never'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Detailed Analytics Tabs */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="border-b border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white">Detailed Analytics</CardTitle>
                <CardDescription className="text-slate-400">
                  Department-wise breakdown with resolver details
                </CardDescription>
              </div>
              <div className="flex items-center gap-3">
                <Filter className="w-4 h-4 text-slate-400" />
                <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
                  <SelectTrigger className="w-48 bg-slate-700 border-slate-600 text-white">
                    <SelectValue placeholder="Filter by department" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="all" className="text-white">All Departments</SelectItem>
                    {getDepartments().map(dept => (
                      <SelectItem key={dept} value={dept} className="text-white">{dept}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="w-full justify-start rounded-none border-b border-slate-700 bg-slate-800/50 p-0 h-auto flex-wrap">
                <TabsTrigger 
                  value="overview" 
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#E97832] data-[state=active]:bg-slate-700/50 px-6 py-3 text-slate-300 data-[state=active]:text-white"
                >
                  Department Overview
                </TabsTrigger>
                <TabsTrigger 
                  value="wip" 
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#E97832] data-[state=active]:bg-slate-700/50 px-6 py-3 text-slate-300 data-[state=active]:text-white"
                >
                  Work in Progress
                </TabsTrigger>
                <TabsTrigger 
                  value="resolved" 
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#E97832] data-[state=active]:bg-slate-700/50 px-6 py-3 text-slate-300 data-[state=active]:text-white"
                >
                  Resolved & Completed
                </TabsTrigger>
                <TabsTrigger 
                  value="resolvers" 
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#E97832] data-[state=active]:bg-slate-700/50 px-6 py-3 text-slate-300 data-[state=active]:text-white"
                >
                  Resolver Activity
                </TabsTrigger>
                <TabsTrigger 
                  value="directory" 
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#E97832] data-[state=active]:bg-slate-700/50 px-6 py-3 text-slate-300 data-[state=active]:text-white flex items-center gap-2"
                  data-testid="staff-directory-tab"
                >
                  <BookUser className="w-4 h-4" />
                  Staff Directory
                </TabsTrigger>
                <TabsTrigger
                  value="feedback"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#E97832] data-[state=active]:bg-slate-700/50 px-6 py-3 text-slate-300 data-[state=active]:text-white flex items-center gap-2"
                  data-testid="feedback-analytics-tab"
                >
                  <MessageSquare className="w-4 h-4" />
                  Feedback Analytics
                </TabsTrigger>
                <TabsTrigger
                  value="advanced"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#E97832] data-[state=active]:bg-slate-700/50 px-6 py-3 text-slate-300 data-[state=active]:text-white flex items-center gap-2"
                  data-testid="advanced-analytics-tab"
                >
                  <Gauge className="w-4 h-4" />
                  Advanced Analytics
                </TabsTrigger>
              </TabsList>

              {/* Department Performance Ranking Tab */}
              <TabsContent value="overview" className="p-4">
                <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                  <div>
                    <h3 className="text-sm font-semibold text-white">Department Performance Ranking</h3>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Includes average time each department takes to resolve a ticket after it breaches SLA (breach → resolved). Auto-refreshes every 25 seconds.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      data-testid="dept-ranking-refresh-badge"
                      className={`text-xs ${rankingRefreshing ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40' : 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'}`}
                    >
                      {rankingRefreshing ? 'Analyzing…' : `Live · next refresh in ${rankingRefreshIn}s`}
                    </Badge>
                    <Button
                      size="sm"
                      onClick={handleManualRefresh}
                      disabled={rankingRefreshing}
                      data-testid="dept-ranking-refresh-btn"
                      className="bg-[#E97832] hover:bg-[#C25E20] text-white text-xs h-7 px-3"
                    >
                      <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${rankingRefreshing ? 'animate-spin' : ''}`} />
                      {rankingRefreshing ? 'Analyzing department totals…' : 'Refresh'}
                    </Button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-700">
                        <th className="text-center py-3 px-3 text-sm font-semibold text-slate-300 w-14">Rank</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300">Department</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-slate-300">Grand Total Tickets</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-slate-300">Grand Total Resolved</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-slate-300">Grand Total Breached</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-slate-300">SLA %</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-slate-300" title="Average time from SLA breach to resolution">Avg Breach Time→Resolved</th>
                        <th className="text-center py-3 px-4 text-sm font-semibold text-slate-300">Avg Rating</th>
                        <th className="text-right py-3 px-4 text-sm font-semibold text-slate-300">Est. Cost</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300" title="Most-frequent breach reason summarised in 1–2 words">Common Breach Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredDepartmentStats().map((dept, idx) => {
                        const breach = formatBreachHours(dept.avg_breach_to_resolved_hours);
                        return (
                          <tr key={dept.department} className="border-b border-slate-700/50 hover:bg-slate-700/30" data-testid={`admin-dept-row-${idx}`}>
                            <td className="py-3 px-3 text-center">
                              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-slate-700 text-slate-200 text-xs font-semibold">
                                {idx + 1}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <span className="text-white font-medium">{dept.department}</span>
                              <Badge variant="outline" className="ml-2 text-[10px] text-slate-400 border-slate-600">
                                {dept.category}
                              </Badge>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className="text-white font-semibold">{dept.total_tickets}</span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className="text-emerald-400">{dept.resolved}</span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className={dept.breached > 0 ? 'text-red-400 font-semibold' : 'text-slate-500'}>
                                {dept.breached}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className={`font-semibold ${
                                dept.sla_compliance >= 90 ? 'text-emerald-400' :
                                dept.sla_compliance >= 70 ? 'text-amber-400' : 'text-red-400'
                              }`}>
                                {dept.sla_compliance}%
                              </span>
                            </td>
                            <td
                              className="py-3 px-4 text-center font-mono text-sm text-amber-300"
                              title={`${breach.tooltip}${dept.breach_resolved_samples ? ` · ${dept.breach_resolved_samples} sample(s)` : ''}`}
                              data-testid={`admin-dept-breach-tat-${idx}`}
                            >
                              {breach.label}
                            </td>
                            <td className="py-3 px-4 text-center" data-testid={`admin-dept-rating-${idx}`}>
                              {dept.avg_rating > 0 ? (
                                <span className="text-yellow-300 font-semibold">
                                  {dept.avg_rating} <span className="text-yellow-400">★</span>
                                </span>
                              ) : (
                                <span className="text-slate-500 text-xs">N/A</span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-right font-mono text-sm text-slate-200" data-testid={`admin-dept-cost-${idx}`}>
                              ₹{(dept.total_cost || 0).toLocaleString('en-IN')}
                            </td>
                            <td className="py-3 px-4 text-left text-sm" data-testid={`admin-dept-breach-reason-${idx}`}>
                              {dept.common_breach_reason ? (
                                <span className="text-rose-300 font-medium">{dept.common_breach_reason}</span>
                              ) : (
                                <span className="text-slate-500 text-xs">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {getFilteredDepartmentStats().length === 0 && (
                    <div className="text-center py-8 text-slate-500">
                      No data available for selected filter
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Work in Progress Tab */}
              <TabsContent value="wip" className="p-4">
                <div className="space-y-3">
                  {dailyStats?.wip_tickets?.length > 0 ? (
                    dailyStats.wip_tickets.map((ticket) => (
                      <div key={ticket.ticket_id} className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-3">
                              <span className="font-bold text-[#E97832]">{ticket.ticket_id}</span>
                              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                                In Progress
                              </Badge>
                              <Badge variant="outline" className="text-slate-400 border-slate-600">
                                {ticket.department}
                              </Badge>
                            </div>
                            <p className="text-slate-400 text-sm mt-1 line-clamp-1">{ticket.description}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-white font-medium">{ticket.resolver_name || 'Unassigned'}</p>
                            <p className="text-slate-500 text-xs">{ticket.resolver_designation || 'N/A'}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      No tickets currently in progress
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Resolved Tab */}
              <TabsContent value="resolved" className="p-4">
                <div className="space-y-3">
                  {dailyStats?.resolved_tickets?.length > 0 ? (
                    dailyStats.resolved_tickets.map((ticket) => (
                      <div key={ticket.ticket_id} className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-3">
                              <span className="font-bold text-emerald-400">{ticket.ticket_id}</span>
                              <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                                Resolved
                              </Badge>
                              <Badge variant="outline" className="text-slate-400 border-slate-600">
                                {ticket.department}
                              </Badge>
                            </div>
                            <p className="text-slate-400 text-sm mt-1 line-clamp-1">{ticket.description}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-white font-medium">{ticket.resolver_name || 'N/A'}</p>
                            <p className="text-slate-500 text-xs">{ticket.resolver_designation || 'N/A'}</p>
                            <p className="text-emerald-400 text-xs mt-1">{ticket.resolved_at}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <CheckCircle2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      No tickets resolved on selected date
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Resolver Activity Tab */}
              <TabsContent value="resolvers" className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Active Resolvers */}
                  <div>
                    <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                      <UserCheck className="w-4 h-4 text-emerald-400" />
                      Active Resolvers
                    </h3>
                    <div className="space-y-2">
                      {resolverActivity.length > 0 ? resolverActivity.map((resolver) => (
                        <div key={`active-${resolver.email || resolver.name}`} className="bg-emerald-900/20 rounded-lg p-3 border border-emerald-800/50">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-white font-medium">{resolver.name}</p>
                              <p className="text-emerald-400 text-xs">{resolver.department}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-emerald-300 text-sm">{resolver.resolved_count} resolved</p>
                              <p className="text-slate-500 text-xs">Last: {resolver.last_active}</p>
                            </div>
                          </div>
                        </div>
                      )) : (
                        <p className="text-slate-500 text-sm">No active resolvers data</p>
                      )}
                    </div>
                  </div>

                  {/* Inactive Resolvers */}
                  <div>
                    <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                      <UserX className="w-4 h-4 text-red-400" />
                      Inactive Resolvers (Need Attention)
                    </h3>
                    <div className="space-y-2">
                      {inactiveResolvers.length > 0 ? inactiveResolvers.map((resolver) => (
                        <div key={`inactive-tab-${resolver.email || resolver.name}`} className="bg-red-900/20 rounded-lg p-3 border border-red-800/50">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-white font-medium">{resolver.name}</p>
                              <p className="text-red-400 text-xs">{resolver.department}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-red-300 text-sm">{resolver.pending_tickets} pending</p>
                              <p className="text-slate-500 text-xs">Last: {resolver.last_active || 'Never'}</p>
                            </div>
                          </div>
                        </div>
                      )) : (
                        <p className="text-emerald-400 text-sm">All resolvers are active!</p>
                      )}
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Staff Directory Tab */}
              <TabsContent value="directory" className="p-4" data-testid="staff-directory-content">
                <div className="space-y-4">
                  {/* Search and Stats Header */}
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-[#E97832]/20 flex items-center justify-center">
                        <BookUser className="w-6 h-6 text-[#E97832]" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">Staff Directory</h3>
                        <p className="text-slate-400 text-sm">{staffCount} registered staff members</p>
                      </div>
                    </div>
                    <div className="relative w-full md:w-80">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Search by name, email, or department..."
                        value={staffSearch}
                        onChange={(e) => setStaffSearch(e.target.value)}
                        className="pl-10 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400"
                        data-testid="staff-search-input"
                      />
                    </div>
                  </div>

                  {/* Staff Cards Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {staffDirectory
                      .filter(staff => {
                        const search = staffSearch.toLowerCase();
                        return !search || 
                          staff.name.toLowerCase().includes(search) ||
                          staff.email.toLowerCase().includes(search) ||
                          staff.designation.toLowerCase().includes(search) ||
                          staff.departments.some(d => d.toLowerCase().includes(search));
                      })
                      .map((staff, index) => (
                        <div 
                          key={`${staff.email}-${index}`}
                          className="bg-slate-700/50 rounded-lg p-4 border border-slate-600 hover:border-[#E97832]/50 transition-colors"
                          data-testid={`staff-card-${staff.email.replace('@', '-at-')}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#E97832] to-orange-600 flex items-center justify-center text-white font-bold text-sm">
                              {staff.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="text-white font-medium truncate">{staff.name}</h4>
                              <p className="text-[#E97832] text-sm">{staff.designation}</p>
                            </div>
                          </div>

                          {/* Email */}
                          <div className="mt-3 flex items-center gap-2 text-slate-300 text-sm">
                            <Mail className="w-4 h-4 text-slate-500 flex-shrink-0" />
                            <a 
                              href={`mailto:${staff.email}`} 
                              className="truncate hover:text-[#E97832] transition-colors"
                            >
                              {staff.email}
                            </a>
                          </div>

                          {/* Alternate emails if any */}
                          {staff.alternate_emails?.length > 0 && (
                            <div className="mt-1 ml-6 text-slate-400 text-xs">
                              Also: {staff.alternate_emails.join(', ')}
                            </div>
                          )}

                          {/* Departments */}
                          <div className="mt-3">
                            <div className="flex items-center gap-2 text-slate-400 text-xs mb-2">
                              <Briefcase className="w-3 h-3" />
                              <span>Departments ({staff.departments.length})</span>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {staff.departments.slice(0, 4).map((dept, deptIndex) => (
                                <Badge 
                                  key={`${dept}-${deptIndex}`}
                                  variant="outline" 
                                  className="text-xs bg-slate-600/50 text-slate-300 border-slate-500"
                                >
                                  {dept}
                                </Badge>
                              ))}
                              {staff.departments.length > 4 && (
                                <Badge 
                                  variant="outline" 
                                  className="text-xs bg-[#E97832]/20 text-[#E97832] border-[#E97832]/30"
                                >
                                  +{staff.departments.length - 4} more
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>

                  {/* Empty state */}
                  {staffDirectory.filter(staff => {
                    const search = staffSearch.toLowerCase();
                    return !search || 
                      staff.name.toLowerCase().includes(search) ||
                      staff.email.toLowerCase().includes(search) ||
                      staff.designation.toLowerCase().includes(search) ||
                      staff.departments.some(d => d.toLowerCase().includes(search));
                  }).length === 0 && (
                    <div className="text-center py-12">
                      <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                      <p className="text-slate-400">No staff members found matching your search.</p>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Feedback Analytics Tab */}
              <TabsContent value="feedback" className="p-4">
                <FeedbackAnalyticsSection departments={getDepartments()} />
              </TabsContent>

              {/* Advanced Analytics Tab */}
              <TabsContent value="advanced" className="p-4">
                <AdvancedAnalyticsSection />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Report Download Panel */}
        <div className="mt-6">
          <ReportDownloadPanel user={user} />
        </div>

        {/* Status Distribution drilldown modal */}
        <StatusDrilldownModal
          status={drilldownStatus}
          open={!!drilldownStatus}
          onClose={() => setDrilldownStatus(null)}
        />
      </main>
    </div>
  );
};

export default AdminAnalyticsDashboard;
